import { z } from 'zod';
import {
  AttachmentSchema,
  MAX_MESSAGES,
  MAX_TEXT_LEN,
  MessageSchema,
  MetaSchema,
  Rfc3339DateTime,
} from './create-chat.js';
import { TurnArtifactSchema } from './workstream-agent.js';

/**
 * `POST /api/v1/workstreams/chat` — a chat turn inside an **existing**
 * workstream. Async (ADR-0037): the BFF submits the turn and returns `202`
 * with a `turnId`; the SPA polls `/workstreams/chat/poll` for the result.
 * Distinct from `/workstreams/create/chat` (ADR-0024, frozen —
 * creates nothing, no `workstreamId`): this endpoint is scoped to a real
 * workstream and also carries the Librarian "invite agent" hint
 * (`agentName` → `AgentInvokeRequest.agentName`).
 *
 * Stateless like the create-flow pair (no router path param — the router
 * only does exact-string method+path matching, see `handlers/router.ts` —
 * so `workstreamId` travels in the body, same as `AgentInvokeRequest`):
 * `messages` is the client-held transcript, resent each call. No
 * server-side persistence yet — `WorkstreamState` lives in-memory in the
 * Python pipeline, persisted in DynamoDB (see ADR-0030 / ADR-0034).
 */
export const WorkstreamChatRequestSchema = z.strictObject({
  workstreamId: z.string().min(1),
  /**
   * Every workstream has at least one focus (product-archaeology §2.8 —
   * `Workstream (1) -> Focus (many) -> Message (many)`). No focus CRUD or
   * persistence yet — the SPA sends the constant `'default'` implicit focus
   * until multi-focus switching lands; the field exists now so the contract
   * doesn't need a breaking change when it does.
   */
  focusId: z.string().min(1),
  messages: z.array(MessageSchema).max(MAX_MESSAGES),
  question: z.string().min(1).max(MAX_TEXT_LEN),
  questionCreatedAt: Rfc3339DateTime.optional(),
  /** Invite-agent hint — forwarded as-is to `AgentInvokeRequest.agentName`. */
  agentName: z.string().optional(),
  /**
   * Turn kind — forwarded to `AgentInvokeRequest.action`. `exitDiscovery`
   * transitions the workstream from discovery mode back to normal routing;
   * absent means a plain chat turn.
   */
  action: z.enum(['chat', 'exitDiscovery']).optional(),
  /**
   * PDF attached to this turn (additive-optional, ADR-0024 §7) — forwarded
   * as-is to `AgentInvokeRequest.attachment`, where the agent runtime feeds
   * it to the model as a Bedrock Converse document block. Turn-scoped: not
   * persisted, not echoed back in the transcript.
   */
  attachment: AttachmentSchema.optional(),
});
export type WorkstreamChatRequest = z.infer<typeof WorkstreamChatRequestSchema>;

/** The Librarian's routing mode for a workstream (see `AgentInvokeRequest.action`). */
export const WorkstreamModeSchema = z.enum(['discovery', 'invite', 'normal']);
export type WorkstreamMode = z.infer<typeof WorkstreamModeSchema>;

/** The consensus tracker's verdict for a turn (Python `ConsensusStatus`). */
export const WorkstreamConsensusSchema = z.enum(['agreed', 'conflicted', 'incomplete']);
export type WorkstreamConsensus = z.infer<typeof WorkstreamConsensusSchema>;

/**
 * Submit ack (ADR-0037): the chat turn now executes asynchronously, so
 * `POST /workstreams/chat` returns `202` with the turn id to poll plus the
 * echoed, server-stamped user message (rendered immediately by the SPA).
 */
export const WorkstreamChatSubmitDataSchema = z.strictObject({
  turnId: z.string().min(1),
  userMessage: MessageSchema,
});
export type WorkstreamChatSubmitData = z.infer<typeof WorkstreamChatSubmitDataSchema>;

/** `{ data, meta }` envelope (tolerant-out, ADR-0024 §11 convention). */
export const WorkstreamChatSubmitResponseSchema = z.looseObject({
  data: WorkstreamChatSubmitDataSchema,
  meta: MetaSchema,
});
export type WorkstreamChatSubmitResponse = z.infer<typeof WorkstreamChatSubmitResponseSchema>;

/** `POST /api/v1/workstreams/chat/poll` — read an async turn's status/result. */
export const WorkstreamChatPollRequestSchema = z.strictObject({
  workstreamId: z.string().min(1),
  turnId: z.string().min(1),
});
export type WorkstreamChatPollRequest = z.infer<typeof WorkstreamChatPollRequestSchema>;

/**
 * One progress checkpoint inside a running turn (ADR-0042) — `kind` is a
 * free string (`routed` / `stage` / `tool` today, additive), `label` the
 * human-readable line the SPA renders verbatim.
 */
export const TurnEventSchema = z.strictObject({
  at: Rfc3339DateTime,
  kind: z.string().min(1).max(40),
  label: z.string().min(1).max(500),
});
export type TurnEvent = z.infer<typeof TurnEventSchema>;

/** Python-side cap (`MAX_TURN_EVENTS`) mirrored so the parse bounds the payload. */
export const MAX_TURN_EVENTS = 50;

/**
 * A completed turn's result — the assistant reply plus the Librarian extras
 * the old sync response carried. `mode` gates the invite / exit-discovery
 * affordances; `consensus`/`conflictDetail` drive the conflict banner;
 * `agents` is the "who handled it" indicator (execution order).
 */
export const WorkstreamTurnResultSchema = z.strictObject({
  reply: MessageSchema,
  mode: WorkstreamModeSchema.optional(),
  consensus: WorkstreamConsensusSchema.optional(),
  conflictDetail: z.string().optional(),
  agents: z.array(z.string()).optional(),
  /** Deliverables written this turn (ADR-0040) — download link + inline preview. */
  artifacts: z.array(TurnArtifactSchema).optional(),
  /**
   * The workstream's cross-turn artifact ledger (ADR-0041, narrowed off
   * `workstreamState.artifacts` per ADR-0042/Stage 3c) — metadata only
   * (`url`/`preview` stripped server-side); feeds the persistent
   * ArtifactWorkspace panel so deliverables outlive the turn that wrote them.
   */
  ledger: z.array(TurnArtifactSchema).optional(),
});
export type WorkstreamTurnResult = z.infer<typeof WorkstreamTurnResultSchema>;

/**
 * Poll payload: `running` while the agent works, `complete` with `result`,
 * `failed` when the turn errored (the SPA surfaces the send-error state).
 * `events` (ADR-0042) is the live-progress trail — present on any status.
 */
export const WorkstreamChatPollDataSchema = z.strictObject({
  status: z.enum(['running', 'complete', 'failed']),
  result: WorkstreamTurnResultSchema.optional(),
  events: z.array(TurnEventSchema).max(MAX_TURN_EVENTS).optional(),
});
export type WorkstreamChatPollData = z.infer<typeof WorkstreamChatPollDataSchema>;

export const WorkstreamChatPollResponseSchema = z.looseObject({
  data: WorkstreamChatPollDataSchema,
  meta: MetaSchema,
});
export type WorkstreamChatPollResponse = z.infer<typeof WorkstreamChatPollResponseSchema>;

/**
 * `data` of `GET /api/v1/workstreams/{id}/messages` — the Drop-7 transcript
 * read path (ADR-0047). The BFF invokes the agent's `listMessages` action,
 * which reads `WorkstreamState.recent_messages` — the message log the
 * summarizer writes every completed turn (newest-40 capped, 30-day state
 * TTL). Chronological (oldest first) so the SPA seeds its transcript as-is.
 * Empty for a never-run or idle-aged-out workstream.
 */
export const ListMessagesDataSchema = z.strictObject({
  messages: z.array(MessageSchema).max(MAX_MESSAGES),
});
export type ListMessagesData = z.infer<typeof ListMessagesDataSchema>;

export const ListMessagesResponseSchema = z.looseObject({
  data: ListMessagesDataSchema,
  meta: MetaSchema,
});
export type ListMessagesResponse = z.infer<typeof ListMessagesResponseSchema>;
